/**
 * Personal edit link (/editar/:token), sent by an admin (ADB-169): the participant completes or
 * corrects their registration form and, on Digital-signature events, signs. The token is not the
 * QR token; it expires and can be revoked, so an invalid link shows a clear message.
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  getEditDocumentUrl,
  getRegistrationEdit,
  updateRegistrationByLink,
  uploadEditDocument,
} from '../publicRegistrationRepository';
import { PublicShell } from '../components/PublicShell';
import { RegistrationEditForm } from '../components/RegistrationEditForm';
import type { DocumentKind, RegistrationEditPayload } from '../types';

export function PublicRegistrationEditPage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Bumped after each save so the form restarts from the data the server now has
  const [version, setVersion] = useState(0);

  const { data: view, isLoading, error } = useQuery({
    queryKey: ['registration_edit', token],
    queryFn: () => getRegistrationEdit(token as string),
    enabled: !!token,
    retry: false,
    gcTime: 0,
  });

  const save = async (payload: RegistrationEditPayload) => {
    if (!token) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateRegistrationByLink(token, payload);
      await queryClient.invalidateQueries({ queryKey: ['registration_edit', token] });
      setVersion((v) => v + 1);
      setSaved(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'No se pudo guardar. Intente nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PublicShell>
      {isLoading && (
        <p className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Cargando su inscripción…
        </p>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error ? error.message : 'El enlace no es válido o ya venció.'}
          </AlertDescription>
        </Alert>
      )}

      {view && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{view.event.name}</CardTitle>
              <CardDescription>
                Complete o corrija los datos de su inscripción
                {view.event.signature_mode === 'DIGITAL' && !view.documents.signature ? ' y firme su formulario' : ''}.
                {view.expires_at && ` Este enlace vence el ${new Date(view.expires_at).toLocaleDateString('es-BO')}.`}
              </CardDescription>
            </CardHeader>
            {saved && (
              <CardContent>
                <div className="flex items-start gap-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  <div className="space-y-2">
                    <p className="font-medium">Sus datos fueron guardados.</p>
                    <p className="text-muted-foreground">
                      Puede cerrar esta página. Si necesita corregir algo más, puede hacerlo abajo mientras el enlace esté
                      vigente.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setSaved(false)}>
                      Entendido
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          <RegistrationEditForm
            key={version}
            view={view}
            mode="link"
            upload={(kind: DocumentKind, file: Blob) => uploadEditDocument(token as string, kind, file)}
            loadDocument={(kind: DocumentKind) => getEditDocumentUrl(token as string, kind)}
            cacheKey={token as string}
            saving={saving}
            error={saveError}
            onSave={save}
          />
        </>
      )}
    </PublicShell>
  );
}
